<template>
  <header class="calm-topbar">
    <span class="calm-topbar__spacer"></span>

    <!-- TH/EN language toggle (Calm segmented) -->
    <div class="calm-topbar__lang" role="group" :aria-label="$t('welcome')">
      <button
        type="button"
        class="calm-topbar__lang-btn"
        :class="{ 'is-active': $i18n.locale === 'th' }"
        @click="th()"
      >
        TH
      </button>
      <button
        type="button"
        class="calm-topbar__lang-btn"
        :class="{ 'is-active': $i18n.locale === 'en' }"
        @click="en()"
      >
        EN
      </button>
    </div>

    <!-- User dropdown (right) — keep base-dropdown for open/close, restyle Calm -->
    <base-dropdown
      menu-on-right
      class="calm-user"
      tag="div"
      title-tag="div"
    >
      <div
        class="calm-user__btn"
        slot="title-container"
        @click.prevent
      >
        <calm-avatar-upload
          class="calm-user__avatar"
          size="sm"
          :editable="false"
          :name="getUser.FName + ' ' + getUser.LName"
          :value="avatarImage"
        />
        <span class="calm-user__meta d-none d-lg-flex">
          <span class="calm-user__name">{{ getUser.FName + ' ' + getUser.LName }} ({{ getUser.Username }})</span>
          <span class="calm-user__branch">{{ getUser.BranchName }}</span>
        </span>
        <i class="fa fa-angle-down calm-user__caret"></i>
      </div>

      <template>
        <div class="calm-usermenu__header">{{ $t("welcome") }}</div>
        <router-link :to="{ name: 'ProfileChange' }" class="calm-usermenu__item">
          <i class="ni ni-single-02"></i>
          <span>{{ $t("myprofile") }}</span>
        </router-link>
        <router-link :to="{ name: 'ProfileChangePassword' }" class="calm-usermenu__item">
          <i class="fa fa-key"></i>
          <span>{{ $t("changepassword") }}</span>
        </router-link>
        <div class="calm-usermenu__sep"></div>
        <a href="#!" class="calm-usermenu__item calm-usermenu__item--danger" @click="logout()">
          <i class="ni ni-user-run"></i>
          <span>{{ $t("auth.signout") }}</span>
        </a>
      </template>
    </base-dropdown>
  </header>
</template>
<script>
import { CollapseTransition } from "vue2-transitions";
import { BaseNav, Modal } from "@/components";
import { mapActions, mapGetters } from "vuex";
import CalmAvatarUpload from "@/components/lmg/CalmAvatarUpload.vue";

export default {
  components: {
    CollapseTransition,
    BaseNav,
    Modal,
    CalmAvatarUpload
  },
  props: {
    type: {
      type: String,
      default: "default", // default|light
      description:
        "Look of the dashboard navbar. Default (Green) or light (gray)"
    }
  },
  async mounted() {
    const u = this.getUser || {};
    if (u.PersonId) {
      try {
        const img = await this.getImage(u.PersonId);
        this.avatarImage = img && img.data ? img.data : "";
      } catch (e) {
        this.avatarImage = "";
      }
    }
  },
  computed: {
    ...mapGetters({
      getUser: "Auth/getUser"
    }),
    routeName() {
      const { name } = this.$route;
      return this.capitalizeFirstLetter(name);
    }
  },
  data() {
    return {
      activeNotifications: false,
      showMenu: false,
      searchModalVisible: false,
      searchQuery: "",
      avatarImage: ""
    };
  },
  methods: {
    ...mapActions({
      signOut: "Auth/signOut",
      getImage: "Person/getImage"
    }),
    capitalizeFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    toggleNotificationDropDown() {
      this.activeNotifications = !this.activeNotifications;
    },
    closeDropDown() {
      this.activeNotifications = false;
    },
    toggleSidebar() {
      this.$sidebar.displaySidebar(!this.$sidebar.showSidebar);
    },
    hideSidebar() {
      this.$sidebar.displaySidebar(false);
    },
    logout() {
      this.$cookie.delete("token");
      this.signOut();
      this.$router.push("/login");
    },
    th() {
      this.$i18n.locale = "th";
      console.log(this.$i18n.locale);
    },
    en() {
      this.$i18n.locale = "en";
    }
  }
};
</script>
<style lang="scss" scoped>
/* ── Calm topbar ──
   White surface + Calm border. Only rendered inside the Calm DashboardLayout,
   and all selectors are namespaced `calm-topbar*` / `calm-user*` so no other
   layout is affected. Tokens (--c-*) come from .calm-layout (DashboardLayout). */
.calm-topbar {
  position: sticky;
  top: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  gap: 12px;
  height: 54px;
  padding: 0 20px;
  background: var(--c-surface);
  border-bottom: 1px solid var(--c-border);
  color: var(--c-text);
  font-family: var(--c-font);
}
.calm-topbar__spacer {
  flex: 1;
}

/* language toggle — self-contained (lives under .calm-layout, not .calm,
   so it must not depend on the .calm-scoped .c-btn kit classes) */
.calm-topbar__lang {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px;
  border: 1px solid var(--c-border);
  border-radius: 8px;
}
.calm-topbar__lang-btn {
  min-width: 38px;
  height: 28px;
  padding: 0 10px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--c-text-soft);
  font-family: var(--c-font);
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: background 140ms ease, color 140ms ease;
}
.calm-topbar__lang-btn:hover {
  color: var(--c-blue-deep);
}
.calm-topbar__lang-btn.is-active {
  background: var(--c-blue);
  color: #fff;
}

/* user menu trigger */
.calm-user {
  position: relative;
  cursor: pointer;
  user-select: none;
}
.calm-user__btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 42px;
  padding: 0 8px;
  border: 1px solid transparent;
  border-radius: 10px;
  background: transparent;
  cursor: pointer;
  transition: background 140ms ease;
}
.calm-user__btn:hover {
  background: var(--c-bg);
}
/* CalmAvatarUpload (size="sm", editable=false) renders its own 40px square
   frame; just keep it from shrinking in the flex row. */
.calm-user__avatar {
  flex: none;
  display: inline-flex;
}
.calm-user__meta {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.2;
  max-width: 220px;
}
.calm-user__name {
  font-size: 13px;
  font-weight: 600;
  color: var(--c-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.calm-user__branch {
  font-size: 11.5px;
  color: var(--c-text-mute);
}
.calm-user__caret {
  color: var(--c-text-mute);
  font-size: 14px;
}

/* ── Calm dropdown menu (reaching base-dropdown's .dropdown-menu / items) ── */
.calm-user ::v-deep .dropdown-menu {
  min-width: 220px;
  margin-top: 6px;
  padding: 6px;
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: 10px;
  box-shadow: 0 8px 28px rgba(44, 62, 80, 0.16);
}
.calm-usermenu__header {
  padding: 8px 10px 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--c-text-mute);
}
.calm-usermenu__item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  border-radius: 8px;
  font-size: 13.5px;
  color: var(--c-text);
  text-decoration: none;
  cursor: pointer;

  i {
    font-size: 14px;
    color: var(--c-text-soft);
    width: 16px;
    text-align: center;
  }
}
.calm-usermenu__item:hover {
  background: var(--c-bg);
  color: var(--c-text);
}
.calm-usermenu__item--danger {
  color: var(--c-danger, #c0573e);
  i {
    color: var(--c-danger, #c0573e);
  }
}
.calm-usermenu__item--danger:hover {
  color: var(--c-danger, #c0573e);
}
.calm-usermenu__sep {
  height: 1px;
  margin: 6px 4px;
  background: var(--c-border);
}

/* router-link active styling reset so it doesn't inherit Argon's colored state */
.calm-user ::v-deep a.router-link-active {
  color: var(--c-text);
}
.calm-user ::v-deep a.router-link-active.calm-usermenu__item--danger {
  color: var(--c-danger, #c0573e);
}

@media (max-width: 1199.98px) {
  /* leave room for the layout's mobile hamburger (top-left) */
  .calm-topbar {
    padding-left: 64px;
  }
  .calm-user__meta {
    display: none !important;
  }
}
</style>
